/**
 * Build-time loader for the vendored link policy (data/link-hierarchy/unit-link-policy.json) and the adapters that turn a
 * page's route data into the engine's inputs. Only imported from getStaticProps (Node), never from render code — Next
 * strips it from the client bundle together with the other fs reads in pages/[id].tsx.
 */
import { readFileSync } from 'fs';
import path from 'path';
import { GroupMember, UnitLinkPolicy, UnitPairBlocks, unitHubBlocks, unitPairBlocks, unitPairH1, unitFileTools, unlinkDisallowedInternalAnchors } from './link-hierarchy';

export const POLICY_FILE = path.resolve(process.cwd(), 'data', 'link-hierarchy', 'unit-link-policy.json');

let _policy: UnitLinkPolicy | null = null;
export function loadUnitLinkPolicy(): UnitLinkPolicy {
    if (_policy) return _policy;
    _policy = JSON.parse(readFileSync(POLICY_FILE, 'utf8')) as UnitLinkPolicy;
    return _policy;
}

const APP = '/unit-converter';

/** Route-data -> engine input. sidePanelLinks is the from-unit group in generator order with self already excluded. */
export function groupFromRouteData(routeData: any): GroupMember[] {
    const links: any[] = routeData.sidePanelLinks || [];
    const converters: any[] = routeData.fromTypeSupportedTargetConverters || [];
    // toUnit abbreviation lives on fromTypeSupportedTargetConverters; join on the readable plural + abbr pair.
    const toUnitByKey = new Map<string, string>();
    for (const c of converters) toUnitByKey.set(`${c.readableToPlural}|${c.readableToAbbr}`, c.toUnit);
    return links.map(l => ({
        toUnit: toUnitByKey.get(`${l.readableToPlural}|${l.readableToAbbr}`) ?? l.readableToAbbr,
        url: l.link,
        h1: unitPairH1(l.readableFromPlural, l.readableFromAbbr, l.readableToPlural, l.readableToAbbr),
    }));
}

const CONTENT_FIELDS = ['faqContent', 'howtoContent', 'pageSummary', 'pageContent', 'fromUnitContent', 'toUnitContent'];

/** Compute the pair page's blocks and apply the policy to the route data (content anchors, trimmed side panel list). */
export function applyUnitPairPolicy(routeData: any): { linkBlocks: UnitPairBlocks; routeData: any } {
    const policy = loadUnitLinkPolicy();
    const page = { url: routeData.url, measure: routeData.from.type, fromUnit: routeData.from.unit, toUnit: routeData.to.unit, group: groupFromRouteData(routeData) };
    const linkBlocks = unitPairBlocks(policy, page);
    const reverse = `${APP}/${String(routeData.id).split('-to-').reverse().join('-to-')}`;
    const hub = `${APP}/${routeData.from.type}`;
    const allowed = new Set<string>([...linkBlocks.linked, ...linkBlocks.popularUnit.map(t => t.href), reverse, hub]);
    const out = { ...routeData };
    for (const f of CONTENT_FIELDS) if (out[f]) out[f] = unlinkDisallowedInternalAnchors(out[f], allowed);
    // Ship only the side-panel entries the page renders (group-links / group-list / neighbours); the full group was a
    // dead __NEXT_DATA__ payload on LV pages once the side panel stopped listing it.
    const keep = new Set(linkBlocks.linked);
    out.sidePanelLinks = (routeData.sidePanelLinks || []).filter((l: any) => keep.has(l.link));
    return { linkBlocks, routeData: out };
}

export function hubLinkBlocks(measure: string) {
    return unitHubBlocks(loadUnitLinkPolicy(), `${APP}/${measure}`);
}

export function rootLinkBlocks() {
    return unitFileTools(loadUnitLinkPolicy(), APP);
}
